import axios from 'axios'

export const state = {
  activeChats: [],    // Array of active chat objects: { recipient_id, page_id, name, messages: [] }
  isChatWindowOpen: false, // Overall container visibility
  isPolling: false,   // To prevent overlapping requests
  pollingInterval: null
}

export const getters = {
  activeChats: (state) => state.activeChats,
  isChatWindowOpen: (state) => state.isChatWindowOpen
}

export const mutations = {
  OPEN_CHAT(state, { recipient_id, page_id, name, contact_picture, platform }) {
    state.isChatWindowOpen = true
    
    // Check if chat is already open
    const existing = state.activeChats.find(c => c.recipient_id === recipient_id && c.page_id === page_id)
    if (!existing) {
      state.activeChats.push({
        recipient_id,
        page_id,
        name,
        contact_picture,
        platform,
        messages: [],
        unread: 0
      })
    }
  },
  CLOSE_CHAT(state, recipient_id) {
    state.activeChats = state.activeChats.filter(c => c.recipient_id !== recipient_id)
    if (state.activeChats.length === 0) {
      state.isChatWindowOpen = false
    }
  },
  TOGGLE_CHAT_WINDOW(state, isOpen) {
    state.isChatWindowOpen = isOpen
  },
  APPEND_MESSAGES(state, { recipient_id, messages }) {
    const chat = state.activeChats.find(c => c.recipient_id === recipient_id)
    if (chat) {
      // Append only new messages by ID
      const existingIds = new Set(chat.messages.map(m => m.messageSID))
      const newMessages = messages.filter(m => !existingIds.has(m.messageSID))
      
      chat.messages.push(...newMessages)
      
      // Sort messages by date
      chat.messages.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn))
      
      // Update unread count if needed
      // If we append messages but chat isn't currently focused, we might increment
      // For now we just add the messages.
    }
  },
  START_POLLING(state) {
    state.isPolling = true
  },
  STOP_POLLING(state) {
    state.isPolling = false
  },
  SET_POLL_INTERVAL(state, interval) {
    state.pollingInterval = interval
  }
}

export const actions = {
  openChat({ commit, dispatch }, chatData) {
    commit('OPEN_CHAT', chatData)
    // Fetch initial messages for this chat
    dispatch('fetchMessages', chatData)
  },
  closeChat({ commit }, recipient_id) {
    commit('CLOSE_CHAT', recipient_id)
  },
  async fetchMessages({ commit, rootState }, { recipient_id, page_id }) {
    try {
      const token = rootState.auth.token || localStorage.getItem('jwt')
      if (!token) return
      
      // We are going to reuse a general endpoint or create one to fetch specific conversation
      // For now, let's assume we use the general getSMSConversation, but we might need a specific one for Meta.
      // Wait, Twilio getConversationSMS uses 'selectedUser' which is the client ID.
      // We will need a new endpoint for fetching Meta conversations! Let's call it /api/comm/meta/conversation
      
      const response = await axios.post('/api/comm/meta/conversation', {
        token: token,
        recipient_id: recipient_id,
        page_id: page_id
      })
      
      if (response.data && Array.isArray(response.data)) {
        commit('APPEND_MESSAGES', { recipient_id, messages: response.data })
      }
    } catch (error) {
      console.error('Failed to fetch Meta messages', error)
    }
  },
  async sendMessage({ rootState }, { recipient_id, page_id, message }) {
    try {
      const token = rootState.auth.token || localStorage.getItem('jwt')
      const response = await axios.post('/api/comm/meta/send', {
        token: token,
        recipient_id,
        page_id,
        message
      })
      
      if (response.data.status === 'success') {
        // We optimistically append it or wait for next poll
        // But the webhook doesn't receive outbound messages unless echo is subscribed.
        // It's safer to just fetchMessages right after sending.
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to send Meta message', error)
      return false
    }
  },
  startPolling({ state, commit, dispatch }) {
    if (state.pollingInterval) return
    
    // Poll every 5 seconds for new messages for ALL active chats
    const interval = setInterval(() => {
      if (state.activeChats.length > 0 && !state.isPolling) {
        commit('START_POLLING')
        
        const fetchPromises = state.activeChats.map(chat => 
          dispatch('fetchMessages', { recipient_id: chat.recipient_id, page_id: chat.page_id })
        )
        
        Promise.all(fetchPromises).finally(() => {
          commit('STOP_POLLING')
        })
      }
    }, 5000)
    
    commit('SET_POLL_INTERVAL', interval)
  },
  stopPolling({ state, commit }) {
    if (state.pollingInterval) {
      clearInterval(state.pollingInterval)
      commit('SET_POLL_INTERVAL', null)
    }
  }
}
